import { onActivated, onDeactivated, ref, watch } from 'vue';
import { useEventListener } from '@vueuse/core';

import { wsReconnectEpoch } from '@/core/apollo/ws-connection';

/**
 * Re-pull a screen's data whenever the user comes back to it.
 *
 * The app is used as an installed PWA whose window stays open for days, and
 * every list here is a surface other people write to. Two gaps follow from that:
 *
 * - a page returned to from the background holds whatever Apollo cached hours
 *   ago, and there is no reload affordance in a standalone PWA window;
 * - GraphQL subscriptions have no backlog, so everything published while the
 *   socket was down is lost — a reconnect is precisely the moment the screen
 *   must stop trusting itself.
 *
 * So this hook fires the caller's revalidation on: the tab becoming visible,
 * the network coming back, and the graphql-ws socket reconnecting. It also
 * returns a `refresh()` for an explicit user-facing control (see
 * `ListRefreshButton`), which is the only path that surfaces a failure — an
 * automatic revalidation stays silent and simply retries on the next resume.
 *
 * Kept-alive pages (`MainLayout`'s `<KeepAlive>`) are deactivated, not
 * unmounted, so their listeners outlive the visit; the `isActive` gate keeps a
 * backgrounded page from re-fetching behind the one the user is looking at.
 * `isActive` starts `true` so this works identically for pages that are never
 * kept alive (where `onActivated` never fires at all).
 */
export const useRevalidateOnResume = (revalidate: () => unknown) => {
  const toast = useToast();

  const refreshing = ref(false);
  const isActive = ref(true);

  onActivated(() => {
    isActive.value = true;
  });

  onDeactivated(() => {
    isActive.value = false;
  });

  const run = async (silent: boolean) => {
    // Overlapping revalidations would race each other's cache writes, and the
    // triggers can genuinely coincide (a reconnect on tab focus fires both).
    if (refreshing.value) {
      return;
    }

    refreshing.value = true;

    try {
      await revalidate();
    } catch (error) {
      if (!silent) {
        toast.add({
          title: 'Failed to refresh',
          description: error instanceof Error ? error.message : 'Unknown error',
          color: 'error',
        });
      }
    } finally {
      refreshing.value = false;
    }
  };

  const revalidateIfResumed = () => {
    if (!isActive.value || document.visibilityState !== 'visible') {
      return;
    }

    void run(true);
  };

  useEventListener(document, 'visibilitychange', revalidateIfResumed);
  useEventListener(window, 'online', revalidateIfResumed);
  watch(wsReconnectEpoch, revalidateIfResumed);

  return {
    /** Bind to a refresh control's `loading`. */
    refreshing,
    /** Explicit user-triggered refresh — reports failures. */
    refresh: () => run(false),
  };
};
